import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Share2, RotateCcw, Download, Trophy, Flame, X, Video } from 'lucide-react';
import ShareableCard from './ShareableCard';
import VideoShareModal from './VideoShareModal';
import { useAuth } from '@/context/AuthContext';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Lightweight confetti — pure DOM, no external deps
const fireConfetti = () => {
  const colors = ['#daae3e', '#fde047', '#ffffff', '#10b981', '#f97316'];
  const c = document.createElement('div');
  c.style.cssText = 'position:fixed;inset:0;pointer-events:none;z-index:9999;overflow:hidden;';
  document.body.appendChild(c);
  for (let i = 0; i < 80; i++) {
    const p = document.createElement('div');
    const size = 6 + Math.random() * 6;
    p.style.cssText = `position:absolute;top:-20px;left:${Math.random() * 100}%;width:${size}px;height:${size}px;background:${colors[i % colors.length]};border-radius:${Math.random() > 0.5 ? '50%' : '2px'};opacity:${0.7 + Math.random() * 0.3};transform:rotate(${Math.random() * 360}deg);`;
    c.appendChild(p);
    p.animate(
      [
        { transform: `translateY(-20px) rotate(0deg)`, opacity: 1 },
        { transform: `translateY(${window.innerHeight + 100}px) rotate(${360 + Math.random() * 720}deg)`, opacity: 0 },
      ],
      { duration: 2500 + Math.random() * 1500, easing: 'cubic-bezier(0.2, 0.8, 0.4, 1)' }
    );
  }
  setTimeout(() => c.remove(), 4500);
};

const Counter = ({ to, suffix = '' }) => {
  const [val, setVal] = useState(0);
  useEffect(() => {
    const start = performance.now();
    const dur = 1200;
    const target = Number(to) || 0;
    let raf;
    const tick = (t) => {
      const p = Math.min(1, (t - start) / dur);
      const eased = 1 - Math.pow(1 - p, 3);
      setVal(target * eased);
      if (p < 1) raf = requestAnimationFrame(tick);
      else setVal(target);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [to]);
  const display = Number.isInteger(Number(to)) ? Math.round(val) : val.toFixed(1);
  return <>{display}{suffix}</>;
};

const ScoreReveal = ({ result, challenge, athlete, onTryAgain, onShare, onDone, cardOpen, onCardClose }) => {
  const { getAuthHeaders } = useAuth();
  const { rank, total_participants, in_top_10, in_top_3, attempt, badge, streak } = result || {};
  const score = attempt?.score;
  const isTime = challenge.scoring_type === 'time';
  const [videoShareOpen, setVideoShareOpen] = useState(false);

  useEffect(() => {
    fireConfetti();
  }, []);

  const handleDownloadOriginal = async () => {
    if (!attempt?.video_url) return;
    try {
      await axios.post(`${API}/challenges/download-event?challenge_id=${challenge.id}&athlete_id=${attempt.athlete_id}&kind=original`, {}, { headers: getAuthHeaders() });
    } catch (e) {}
    window.open(attempt.video_url, '_blank');
  };

  const handleDownloadBranded = async () => {
    if (!attempt?.branded_video_url) return handleDownloadOriginal();
    try {
      await axios.post(`${API}/challenges/download-event?challenge_id=${challenge.id}&athlete_id=${attempt.athlete_id}&kind=branded`, {}, { headers: getAuthHeaders() });
    } catch (e) {}
    window.open(attempt.branded_video_url, '_blank');
  };

  return (
    <>
      <div className="min-h-[70vh] flex items-center justify-center" data-testid="score-reveal">
        <div className="max-w-2xl w-full text-center space-y-8">
          <motion.div initial={{ scale: 0.5, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} transition={{ type: 'spring', stiffness: 200, damping: 15 }}>
            <div className="inline-flex items-center gap-2 bg-primary/20 border border-primary/40 rounded-full px-4 py-1.5 mb-4">
              <Trophy size={14} className="text-primary" />
              <span className="text-[10px] uppercase tracking-[0.3em] text-primary font-bold">Challenge Completed</span>
            </div>
          </motion.div>

          <motion.div initial={{ y: 30, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ delay: 0.2, duration: 0.6 }}>
            <p className="text-xs uppercase tracking-widest text-muted-foreground mb-2">Your Score</p>
            <p className="font-heading text-7xl md:text-9xl text-primary tracking-tight" data-testid="reveal-score">
              <Counter to={score} suffix={isTime ? 's' : ''} />
            </p>
          </motion.div>

          <motion.div initial={{ y: 30, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ delay: 0.6, duration: 0.6 }}>
            <p className="text-xs uppercase tracking-widest text-muted-foreground mb-2">Current Rank</p>
            <p className="font-heading text-5xl md:text-6xl text-white" data-testid="reveal-rank">
              #{rank || '—'}
            </p>
          </motion.div>

          {in_top_3 && (
            <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ delay: 1, type: 'spring' }}>
              <div className="inline-block bg-gradient-to-r from-amber-500 to-yellow-300 text-black font-heading text-xl uppercase tracking-widest px-5 py-2 rounded-sm">
                🥇 Top 3 This Week
              </div>
            </motion.div>
          )}
          {!in_top_3 && in_top_10 && (
            <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ delay: 1, type: 'spring' }}>
              <div className="inline-block bg-primary/20 border border-primary/40 text-primary font-heading text-xl uppercase tracking-widest px-5 py-2 rounded-sm">
                🔥 Top 10 This Week
              </div>
            </motion.div>
          )}

          {badge && (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.2 }}>
              <p className="text-sm text-muted-foreground">
                {badge.icon} <span className="text-primary font-bold">{badge.label}</span> · {streak} weeks in a row
              </p>
            </motion.div>
          )}

          <motion.div initial={{ y: 30, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ delay: 1.4 }}
            className="flex flex-col sm:flex-row items-center justify-center gap-3 pt-4 flex-wrap"
          >
            <Button onClick={() => setVideoShareOpen(true)} className="bg-primary text-black hover:bg-primary/90 uppercase tracking-wider font-bold h-12 w-full sm:w-auto px-6" data-testid="reveal-share-video-btn">
              <Video size={16} className="mr-2" /> Share Video
            </Button>
            <Button onClick={onShare} variant="outline" className="border-primary/40 text-primary hover:bg-primary/10 uppercase tracking-wider h-12 w-full sm:w-auto" data-testid="reveal-share-card-btn">
              <Share2 size={16} className="mr-2" /> Share Card
            </Button>
            <Button onClick={handleDownloadBranded} variant="ghost" className="uppercase tracking-wider h-12 w-full sm:w-auto" data-testid="reveal-download-btn">
              <Download size={16} className="mr-2" /> Download
            </Button>
            <Button onClick={onTryAgain} variant="ghost" className="uppercase tracking-wider h-12 w-full sm:w-auto" data-testid="reveal-tryagain-btn">
              <RotateCcw size={16} className="mr-2" /> Try Again
            </Button>
          </motion.div>

          <button onClick={onDone} className="text-xs text-muted-foreground hover:text-primary uppercase tracking-widest mt-6" data-testid="reveal-done-btn">
            ← Back to Challenge
          </button>
        </div>
      </div>

      {cardOpen && (
        <ShareableCard
          challenge={challenge}
          athlete={{ ...athlete, name: athlete?.athlete_name || athlete?.name }}
          rank={rank}
          score={score}
          totalParticipants={total_participants}
          onClose={onCardClose}
        />
      )}

      <VideoShareModal
        open={videoShareOpen}
        onClose={() => setVideoShareOpen(false)}
        attempt={attempt}
        challenge={challenge}
        athlete={{ ...athlete, id: attempt?.athlete_id, name: athlete?.athlete_name || athlete?.name }}
        rank={rank}
      />
    </>
  );
};

export default ScoreReveal;
